import React, { useState } from 'react';
import PostBrandLabel from 'components/PostBrandLabel/PostBrandLabel';
import { 
  MDBCard, 
  MDBCardBody, 
  MDBContainer, 
  MDBIcon,
  MDBBadge,
  MDBTypography,
  MDBRow,
  MDBCol,
  MDBTable,
  MDBTableHead,
  MDBTableBody,
  MDBPagination,
  MDBPageItem,
  MDBPageNav,
} from 'mdbreact';
import _ from 'lodash';
import './PostListView.scss';
import { PlatformIcon as SharedPlatformIcon, formatPostTime, pickPostThumb, PubTypeBadge, StatusBadge as SharedStatusBadge, PostThumb, postPlatforms } from 'helpers/postDisplay';
import { canDeletePost } from 'helpers/postDeletable';
import { archiveControl } from 'helpers/postArchive';

// Adding custom styles for sticky header
const tableHeaderStyle = {
  position: 'sticky',
  top: 0,
  backgroundColor: '#fff',
  zIndex: 1,
  borderBottom: '2px solid #e0e0e0',
  fontWeight: 'bold'
};

// Platform icons component — delegates to the shared helper so list +
// kanban + calendar always render the SAME color for the SAME platform.
const PlatformIcon = SharedPlatformIcon;

// Status badge — delegates to the shared helper. The previous map
// was bidlight-era statuses (pending_subject / approved_subject) that
// never matched Post.queueStatus, so every row showed the fallback.
const StatusBadge = ({ item, source = 'post' }) => (
  <SharedStatusBadge item={item} source={source} size={11} />
);

// Post content preview component. Shown inside the expandable row's body
// so the full post text is visible — wrap long content rather than
// truncating it. wordBreak handles single-token strings (URLs, hash
// chains) that would otherwise overflow horizontally and add scrollbars
// to the table.
const PostContentPreview = ({ post }) => {
  const getContentIcon = () => {
    if (!post) return 'file-alt';
    if (post.isVideo) return 'video';
    if (post.isImage) return 'image';
    return 'file-alt';
  };

  if (!post) return null;

  return (
    <div className="d-flex align-items-start">
      <MDBIcon icon={getContentIcon()} className="mr-2 mt-1" style={{ flex: '0 0 auto' }} />
      <p
        className="mb-0"
        style={{
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          overflowWrap: 'anywhere',
          flex: 1,
          minWidth: 0,
        }}
      >
        {post.textContent || post.description || ''}
      </p>
    </div>
  );
};

// Expandable row component
const ExpandableRow = ({ post, onEdit, onDelete, onArchive, isExpanded, onToggle, source }) => {
  return (
    <>
      {/* Clicking the row opens the edit/preview modal. The chevron
          (and the action icons) stop propagation and keep their own
          behavior — the chevron expands the inline preview. */}
      <tr className="cursor-pointer" onClick={() => onEdit(post)}>
        <td>
          <StatusBadge item={post} source={source} />
        </td>
       {source==='post' && <td>
          <div className="d-flex flex-wrap align-items-center" style={{ gap: 6 }}>
            <PubTypeBadge type={post?.type} size={10} />
            {/* Resolve via the shared helper so auto-generated posts (which
                store socialPlatforms slugs and no populated targetedSocialAccounts)
                show their platform logos on first render, like edited posts do. */}
            {postPlatforms(post).map((p, i) =>
              <PlatformIcon key={i} platform={p} size={18} />
            )}
          </div>
        </td>}
        <td>
          <div className="d-flex align-items-center">
            {/* Inline thumbnail when the post has media — same source
                the calendar + kanban views read so all three stay
                visually consistent. */}
            {pickPostThumb(post) && (
              <div style={{ width: 48, height: 48, marginRight: 10, borderRadius: 4, overflow: 'hidden', background: '#000', flex: '0 0 auto', position: 'relative' }}>
                <PostThumb post={post} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            )}
            <div className="d-flex justify-content-between align-items-center" style={{ flex: 1, minWidth: 0 }}>
              <strong style={{ overflowWrap: 'anywhere', wordBreak: 'break-word', minWidth: 0 }}>
                {source === 'post' && <PostBrandLabel brandId={post.brand_id} brandName={post.brandName} />}
                {post?.title || 'Untitled Post'}
              </strong>
              <MDBIcon
                icon={isExpanded ? 'chevron-up' : 'chevron-down'}
                className="ml-2 cursor-pointer"
                style={{ flex: '0 0 auto' }}
                title={isExpanded ? 'Collapse' : 'Expand preview'}
                onClick={(e) => { e.stopPropagation(); onToggle(); }}
              />
            </div>
          </div>
        </td>
        <td>
          {formatPostTime(source === 'post' ? (post?.scheduled_at || post?.createdAt) : post?.createdAt)}
        </td>
        <td>
          <div className="d-flex">
            <MDBIcon icon="share-alt" className="mx-2 cursor-pointer" onClick={(e) => e.stopPropagation()} />
            <MDBIcon icon="check-circle" className="mx-2 cursor-pointer" onClick={(e) => e.stopPropagation()} />
            <MDBIcon icon="edit" className="mx-2 cursor-pointer" onClick={(e) => {e.stopPropagation(); onEdit(post);}} />
            {/* Archive / un-archive — same control as the Calendar +
                Kanban views (helpers/postArchive). Post-only; wired only
                when the parent passes onArchive. */}
            {source === 'post' && onArchive && (() => {
              const a = archiveControl(post);
              return (
                <span
                  role="button"
                  className="mx-2 cursor-pointer"
                  title={a.title}
                  style={{ fontSize: 14, lineHeight: 1, color: a.archived ? '#7c3aed' : '#9aa0a6' }}
                  onClick={(e) => { e.stopPropagation(); onArchive(post); }}
                >
                  {a.label}
                </span>
              );
            })()}
            {/* Delete is only rendered when the parent wires onDelete.
                For posts, hide it once the post has actually been
                published — deleting the row would just hide the audit
                trail without unpublishing on the platform. Ideas don't
                have a "posted" state, so always show for source==='idea'. */}
            {onDelete && (source !== 'post' || canDeletePost(post)) && (
              <MDBIcon
                icon="trash"
                className="mx-2 cursor-pointer text-danger"
                onClick={(e) => { e.stopPropagation(); onDelete(post); }}
              />
            )}
          </div>
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan="5">
            <div className="p-4 bg-light">
              <PostContentPreview post={post} />
              <div className="mt-3">
                <h6 className="font-weight-bold">Additional Details:</h6>
                <div className="mt-2">
                  {source === 'idea' ? 
                    <p className="mb-1"><strong>Prompt:</strong> {post?.prompt || ''}</p>
                    : null
                  }
                  <p className="mb-1"><strong>Created:</strong> {new Date(source === 'idea' ? post?.createdAt : post?.created_at).toLocaleString()}</p>
                  <p className="mb-1"><strong>Last Modified:</strong> {new Date(source === 'idea' ? post?.updatedAt : post?.updated_at).toLocaleString()}</p>
                  {source === 'post' ? 
                    <p className="mb-1"><strong>Content Type:</strong> {post?.type || 'Text'}</p>
                    : <p className="mb-1"><strong>Approval Email Sent:</strong> {post?.approvalEmailSent ? 'Yes' : 'No'}</p>
                  }
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// Main list view component with sticky headers and pagination
const PostListView = ({ posts = [], onEdit, onDelete, onArchive, source='post' }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage, setPostsPerPage] = useState(5);

  const onSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const toggleRow = (postId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(postId)) {
      newExpandedRows.delete(postId);
    } else {
      newExpandedRows.add(postId);
    }
    setExpandedRows(newExpandedRows);
  };

  const getSortedPosts = () => {
    if (!sortConfig.key) return posts;

    return [...posts].sort((a, b) => {
      let aValue = _.get(a, sortConfig.key);
      let bValue = _.get(b, sortConfig.key);

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <MDBIcon icon="sort" className="ml-1 text-muted" />;
    return (
      <MDBIcon 
        icon={sortConfig.direction === 'asc' ? 'sort-up' : 'sort-down'} 
        className="ml-1"
      />
    );
  };

  // Pagination logic
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = getSortedPosts().slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(posts.length / postsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <MDBContainer fluid>
      <MDBCard className="mt-4 mb-4">
        <MDBCardBody>
          <MDBRow className="mb-4 align-items-center">
            <MDBCol md="6">
              <MDBTypography tag="h2" className="h2-responsive mb-0">
                {source=== 'post' ? 'Social Media Posts' : 'All Ideas'}
              </MDBTypography>
              <p className="text-muted mb-0">
                Total {source==='post' ? 'posts' : 'ideas'}: {posts.length}
              </p>
            </MDBCol>
            <MDBCol md="6" className="text-right">
              <select 
                className="browser-default custom-select w-auto"
                value={postsPerPage}
                onChange={(e) => {
                  setPostsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value="5">5 per page</option>
                <option value="10">10 per page</option>
                <option value="20">20 per page</option>
                <option value="50">50 per page</option>
              </select>
            </MDBCol>
          </MDBRow>

          <div className="table-responsive" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <MDBTable hover>
              <MDBTableHead>
                <tr>
                  <th onClick={() => onSort('status')} className="cursor-pointer" style={{position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1}}>
                    Status {renderSortIcon('status')}
                  </th>
                  {source === 'post' && <th onClick={() => onSort('socialAccount.platform')} className="cursor-pointer" style={{position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1}}>
                    Platform {renderSortIcon('socialAccount.platform')}
                  </th>}
                  <th onClick={() => onSort('title')} className="cursor-pointer" style={{position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1}}>
                    Title {renderSortIcon('title')}
                  </th>
                  <th onClick={() => onSort('postDate')} className="cursor-pointer" style={{position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1}}>
                    {source === 'post' ? 'Post' : 'Idea Creation'} Date {renderSortIcon('postDate')}
                  </th>
                  <th style={{position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1}}>Actions</th>
                </tr>
              </MDBTableHead>
              <MDBTableBody>
                {currentPosts.map((post, index) => (
                  <ExpandableRow
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onArchive={onArchive}
                    source={source}
                    key={post?._id || index}
                    post={post}
                    isExpanded={expandedRows.has(post?._id || index)}
                    onToggle={() => toggleRow(post?._id || index)}
                  />
                ))}
              </MDBTableBody>
            </MDBTable>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-4">
            <div className="text-muted">
              Showing {indexOfFirstPost + 1} to {Math.min(indexOfLastPost, posts.length)} of {posts.length} entries
            </div>
            <MDBPagination className="mb-0">
              <MDBPageItem disabled={currentPage === 1}>
                <MDBPageNav 
                  aria-label="Previous"
                  onClick={() => paginate(currentPage - 1)}
                >
                  <span aria-hidden="true">&laquo;</span>
                </MDBPageNav>
              </MDBPageItem>
              
              {[...Array(totalPages)].map((_, index) => (
                <MDBPageItem 
                  key={index + 1} 
                  active={currentPage === index + 1}
                  onClick={() => paginate(index + 1)}
                >
                  <MDBPageNav>
                    {index + 1}
                  </MDBPageNav>
                </MDBPageItem>
              ))}

              <MDBPageItem disabled={currentPage === totalPages}>
                <MDBPageNav 
                  aria-label="Next"
                  onClick={() => paginate(currentPage + 1)}
                >
                  <span aria-hidden="true">&raquo;</span>
                </MDBPageNav>
              </MDBPageItem>
            </MDBPagination>
          </div>
        </MDBCardBody>
      </MDBCard>
    </MDBContainer>
  );
};

export default PostListView;